<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { LumiIcon, Menu, useTheme, type MenuItem } from '@lumi/ui';
import { useTenantsStore, type Tenant } from '@/stores/tenants';
import { useAuthStore } from '@/stores/auth';
import { appendSessionHandoff, tenantWorkspaceUrl } from '@/utils/module-links';

// The sidebar footer's tenant switcher (#468): mounted ONLY for a multi-tenant
// member, above the account row (AppChrome gates it on `tenantsStore.isMultiTenant`
// — a single-tenant member gets nothing here, the same "show nothing" the header
// used to give them; the workspace-wide support-access banner in AppChrome
// already covers the ADR 0041 transparency duty, so dropping this component's
// own supportAccess marker for the single-tenant case loses nothing). Each
// tenant is its own host/DB, so picking a DIFFERENT tenant on a per-tenant
// (prod) host is a full-page SSO handoff to that tenant's workspace (where the
// fan-out is same-origin + live); on the bare/dev host it re-scopes in place.
// The lib's Menu flips the panel ABOVE the trigger when the viewport has no
// room below: this sits at the very bottom of the sidebar.
const store = useTenantsStore();
const auth = useAuthStore();
const { theme } = useTheme();
const { t, locale } = useI18n();

function formatUntil(value?: string | null): string {
  return value ? new Date(value).toLocaleString() : '';
}

const items = computed<MenuItem[]>(() =>
  store.tenants.map((tn) => ({
    key: tn.id,
    label: tn.name,
    checked: tn.id === store.activeTenantId,
    hint: tn.supportAccess
      ? `${t('tenants.supportAccessBadge')} · ${t('tenants.supportAccessUntil', { until: formatUntil(tn.supportAccessExpiresAt) })}`
      : undefined,
  })),
);

function onSelect(id: string) {
  const tenant = store.tenants.find((tn) => tn.id === id);
  if (tenant) pick(tenant);
}

function pick(tenant: Tenant) {
  const redirectTo = store.selectTenant(tenant);
  if (redirectTo) redirect(redirectTo);
}

function redirect(tenant: Tenant) {
  // Another tenant's workspace is a different host in prod (cross-origin →
  // appendSessionHandoff forwards the token pair); on the bare/dev host the
  // switch is same-origin and carries only the context params.
  const url = new URL(tenantWorkspaceUrl(tenant.slug));
  appendSessionHandoff(
    url,
    { token: auth.token, refreshToken: auth.refreshToken },
    { tenantSlug: tenant.slug, locale: locale.value, theme: theme.value },
  );
  window.location.href = url.toString();
}
</script>

<template>
  <div class="px-1.5 py-1.5">
    <!-- The lib's Menu with a wide trigger: the active workspace is the current
         radio item. A workspace reached through a consented support grant (ADR
         0041) is not the supporter's own; the item says so, with the deadline. -->
    <Menu :items="items" :label="t('tenants.switcherLabel')" block align="start" @select="onSelect">
      <span class="flex w-full items-center gap-2 px-2 py-1.5 text-sm font-medium text-heading">
        <LumiIcon name="company" class="h-4 w-4 shrink-0 text-tertiary" />
        <span class="flex-1 truncate text-left">{{ store.activeTenant?.name }}</span>
        <LumiIcon name="switcher" class="h-4 w-4 shrink-0 text-tertiary" />
      </span>
    </Menu>
  </div>
</template>
